import { useEffect, useId, useState, type CSSProperties } from 'react';
import { useData } from '../store/data';
import { useSpeakingBusy } from '../hooks/useSay';
import { useMascotReaction } from '../lib/mascotReaction';
import { loadSvg, type ParsedSvg } from '../lib/mascotSvg';
import { imgSrc } from '../lib/image';
import type { Mascota, MascotaFaceColor, MascotaSection } from '../types';
import { MascotBody } from './MascotBody';
import { FaceLayer } from './MascotFace';
import './Mascot.css';

/**
 * Una mascota (Admin → Mascotas): el cuerpo es un SVG subido —saneado, ver
 * lib/mascotSvg.ts— y la cara se dibuja acá, en capas propias, para poder
 * animarla. Dónde va la cara y con qué colores sale de la configuración.
 *
 * Es decorativa (aria-hidden) y no se toca: sin borde ni sombra de botón
 * (DESIGN.md, reglas 6 y 7).
 */
export type MascotMood = 'idle' | 'talking' | 'happy' | 'oops' | 'thinking';
export const MASCOT_MOODS: MascotMood[] = ['idle', 'talking', 'happy', 'oops', 'thinking'];

type Props = {
  mascota: Mascota;
  mood?: MascotMood;
  /** `full` de cuerpo entero; `bust` el encuadre de medio cuerpo de la mascota. */
  framing?: 'full' | 'bust';
  className?: string;
};

const FACE_VARS: Record<MascotaFaceColor, string> = {
  ink: '--mascot-ink',
  eyeWhite: '--mascot-eye-white',
  lip: '--mascot-lip',
  mouth: '--mascot-mouth',
  tongue: '--mascot-tongue',
  blush: '--mascot-blush',
};

export function Mascot({ mascota, mood = 'idle', framing = 'full', className = '' }: Props) {
  const url = imgSrc(mascota.body);
  const [svg, setSvg] = useState<{ url: string; data: ParsedSvg } | null>(null);
  const clip = useId();

  useEffect(() => {
    let alive = true;
    loadSvg(url).then((data) => alive && setSvg({ url, data })).catch(() => {});
    return () => { alive = false; };
  }, [url]);

  const body = svg?.url === url ? svg.data : null;
  const viewBox = framing === 'bust' ? mascota.bust : body?.viewBox;

  // Los colores de la cara pisan los tokens por defecto (tokens.css §16).
  const style = {} as Record<string, string>;
  for (const [role, value] of Object.entries(mascota.faceColors)) {
    if (value) style[FACE_VARS[role as MascotaFaceColor]] = value;
  }

  return (
    <svg
      className={`mascot mascot--${framing} m-${mood} ${className}`}
      viewBox={viewBox?.join(' ') ?? mascota.bust.join(' ')}
      style={style as CSSProperties}
      aria-hidden="true"
      focusable="false"
    >
      <g className="m-figure">
        {body && <MascotBody root={body.root} colors={mascota.colors} />}
        {body && mascota.face.enabled && <FaceLayer bodyUrl={mascota.body} face={mascota.face} clip={clip} mood={mood} />}
      </g>
    </svg>
  );
}

/** La mascota que el admin puso en esta sección, hablando mientras suena algo. */
export function SectionMascot({ section }: { section: MascotaSection }) {
  const { mascotas } = useData();
  const reading = useSpeakingBusy();
  const mascota = mascotas.mascotas.find((m) => m.id === mascotas.placements[section]);
  if (!mascota) return null;
  return <Mascot mascota={mascota} mood={reading ? 'talking' : 'idle'} framing="bust" />;
}

/**
 * La mascota de los juegos (`<GameShell>`): reacciona a la última respuesta
 * (contenta con un acierto, "uy" con un error, ver lib/mascotReaction.ts) y si no,
 * habla mientras suena algo. En los juegos de teoría de la mente nunca pone "uy":
 * ahí no se llama a `answerWrong()` (no hay error, hay repaso).
 */
export function GameMascot({ className = '' }: { className?: string }) {
  const { mascotas } = useData();
  const reading = useSpeakingBusy();
  const reaction = useMascotReaction();
  const mascota = mascotas.mascotas.find((m) => m.id === mascotas.placements.juegos);
  const [wondering, setWondering] = useState(false);

  // Un rato sin tocar nada: se queda pensando (mira para arriba), como esperando
  // la próxima jugada. Cualquier reacción o lectura la despierta. No dice nada ni
  // insiste: es una cara, no un recordatorio.
  useEffect(() => {
    setWondering(false);
    if (reaction || reading) return;
    const t = window.setTimeout(() => setWondering(true), IDLE_WONDER_MS);
    return () => window.clearTimeout(t);
  }, [reaction, reading]);

  if (!mascota) return null;
  const mood: MascotMood = reaction?.mood ?? (reading ? 'talking' : wondering ? 'thinking' : 'idle');
  return (
    <Mascot
      mascota={mascota}
      mood={mood}
      framing="bust"
      className={`${className} ${reaction?.streak ? 'm-streak' : ''}`}
    />
  );
}

const IDLE_WONDER_MS = 20000;

/**
 * Una mascota cualquiera de las que el admin ubicó (la de juegos primero), para
 * lugares que no tienen sección propia: el festejo y los estados vacíos.
 */
export function usePlacedMascot(): Mascota | null {
  const { mascotas } = useData();
  const order: MascotaSection[] = ['juegos', 'tarjetas', 'letras', 'silabas'];
  for (const section of order) {
    const m = mascotas.mascotas.find((x) => x.id === mascotas.placements[section]);
    if (m) return m;
  }
  return null;
}
